import { Component, OnInit } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ClientApiAdminClient, ClientApiAdminService, ClientApiDocsLinks } from '../../../services/client-api-admin.service';

interface ClientApiChecklistItem {
  label: string;
  status: 'ready' | 'next' | 'blocked';
  detail: string;
}

interface ClientApiScopeItem {
  scope: string;
  purpose: string;
}

interface ClientApiLink {
  label: string;
  url: string;
}

@Component({
  selector: 'app-client-api-onboarding',
  templateUrl: './client-api-onboarding.component.html',
  styleUrls: ['./client-api-onboarding.component.scss']
})
export class ClientApiOnboardingComponent implements OnInit {
  private readonly clientApiEnvironment = environment as any;

  environmentName = 'local';
  currentAppApiUrl = environment.apiURL || environment.$api_get_url;

  readonly sandboxBaseUrl = this.clientApiEnvironment.clientApiBackvaultURL || 'https://client-api.backvault.com.au';
  readonly productionBaseUrl = this.clientApiEnvironment.clientApiProductionURL || 'https://client-api.sharmangroup.au';
  readonly localBaseUrl = this.clientApiEnvironment.clientApiLocalURL || 'http://localhost:8088';

  selectedBaseUrl = '';
  adminLoading = false;
  adminMessage = '';
  adminError = '';
  generatedSecret = '';
  generatedClientUuid = '';
  generatedClientName = '';
  generatedEnvironment = '';
  generatedBrandId = 0;
  generatedAt = '';
  statusData: any = null;
  operationsData: any = null;
  globalAccessControl: any = null;
  globalAccessReason = '';
  adminDocsLinks: ClientApiDocsLinks | null = null;
  clients: ClientApiAdminClient[] = [];
  createModel = {
    name: '',
    brand_id: 94,
    so_user_id: null as number | null,
    environment_mode: 'sandbox',
    sync_enabled: false,
    status: 'active',
    scopes: 'jobs:create,jobs:read,subjects:read,client-api:logs:read,docs:read'
  };

  readonly requiredFields: ClientApiChecklistItem[] = [
    {
      label: 'SO brand link',
      status: 'ready',
      detail: 'Each API client must be tied to one SO brand_id before tokens are issued.'
    },
    {
      label: 'SO API user',
      status: 'ready',
      detail: 'Map the credential to a user_id from the same brand for created_by, logs and automation variables.'
    },
    {
      label: 'Environment mode',
      status: 'ready',
      detail: 'Sandbox and live are isolated per credential. Use sandbox for production proof before enabling live SO writes.'
    },
    {
      label: 'Scopes',
      status: 'ready',
      detail: 'Assign only the routes the client needs, then review scopes during key rotation.'
    },
    {
      label: 'SO lookup access',
      status: 'ready',
      detail: 'Brand-scoped SO lookup routes are available when the selected environment has its SO worker connection configured.'
    },
    {
      label: 'Secret rotation',
      status: 'ready',
      detail: 'Creation and rotation run through the SO Admin Broker. Angular never stores Client API administrator secrets.'
    }
  ];

  readonly scopes: ClientApiScopeItem[] = [
    { scope: 'jobs:create', purpose: 'Stage new client job requests.' },
    { scope: 'jobs:read', purpose: 'Read Client API job ledger and job lookup routes.' },
    { scope: 'subjects:read', purpose: 'Read subject lookup routes and job subject lists.' },
    { scope: 'docs:read', purpose: 'Issue longer-lived docs-only tokens for approved developers.' },
    { scope: 'notes:read', purpose: 'Read notes submitted through the Client API ledger.' },
    { scope: 'notes:create', purpose: 'Submit job-level and subject-level notes.' },
    { scope: 'documents:read', purpose: 'Read document sync records from the Client API ledger.' },
    { scope: 'documents:upload', purpose: 'Upload job-level, subject-level, or multi-file document packages.' },
    { scope: 'actions:update_request', purpose: 'Request a job or subject update through the established SO workflow.' },
    { scope: 'actions:subject_workflow', purpose: 'Request hold, remove-hold, skip-trace, and re-open actions for a client subject.' },
    { scope: 'actions:close_file', purpose: 'Request job or subject closure and invoicing through the established SO workflow.' },
    { scope: 'client-api:logs:read', purpose: 'Read Client API request logs for the approved client.' }
  ];

  constructor(private clientApiAdmin: ClientApiAdminService) {}

  get links(): ClientApiLink[] {
    const environmentLabel = this.environmentName === 'production'
      ? 'Production'
      : (this.environmentName === 'backvault' ? 'Backvault' : 'Local');
    const baseUrl = this.selectedBaseUrl.replace(/\/+$/, '');

    return [
      { label: environmentLabel + ' Client API Health', url: baseUrl + '/health' },
      { label: environmentLabel + ' Client API', url: baseUrl },
      { label: environmentLabel + ' API Access & Controls', url: this.clientApiAdmin.portalUrlForBrand(Number(this.createModel.brand_id), baseUrl) },
      { label: 'My Investigations Example', url: this.clientApiAdmin.sampleAppUrlFor(baseUrl) }
    ];
  }

  ngOnInit(): void {
    this.selectedBaseUrl = this.clientApiAdmin.selectedBaseUrl;
    const selected = this.selectedBaseUrl.replace(/\/+$/, '');
    if (selected === this.sandboxBaseUrl.replace(/\/+$/, '')) {
      this.environmentName = 'backvault';
    } else if (selected === this.productionBaseUrl.replace(/\/+$/, '')) {
      this.environmentName = 'production';
    }
    this.loadGlobalAccessControl();
  }

  loadGlobalAccessControl(showMessage: boolean = false): void {
    this.adminLoading = true;
    this.adminError = '';
    this.clientApiAdmin.accessControls().subscribe({
      next: response => {
        this.globalAccessControl = response && response.data ? response.data : null;
        const control = this.globalAccessControl && this.globalAccessControl.global;
        this.globalAccessReason = control && control.reason ? control.reason : '';
        this.adminLoading = false;
        if (showMessage) {
          this.adminMessage = 'Global Client API security control loaded.';
        }
      },
      error: error => this.handleAdminError(error, 'Global Client API security control failed to load.')
    });
  }

  setGlobalAccessBlocked(blocked: boolean): void {
    const reason = this.globalAccessReason.trim();
    if (blocked && !reason) {
      this.adminError = 'Enter an incident or operational reason before applying the global block.';
      this.adminMessage = '';
      return;
    }

    const action = blocked ? 'BLOCK all external Client API access' : 'RESTORE external Client API access';
    if (!window.confirm('Confirm: ' + action + ' in ' + this.environmentName + '?')) {
      return;
    }

    this.adminLoading = true;
    this.adminMessage = '';
    this.adminError = '';
    this.clientApiAdmin.setGlobalAccessControl(blocked, reason).subscribe({
      next: response => {
        this.globalAccessControl = response && response.data ? response.data : null;
        this.adminLoading = false;
        this.adminMessage = blocked
          ? 'Global Client API block applied. External requests and queued processing are paused.'
          : 'Global Client API block removed. Eligible requests and queued processing can resume.';
      },
      error: error => this.handleAdminError(error, 'Global Client API security control could not be updated.')
    });
  }

  statusClass(status: string): string {
    if (status === 'ready') {
      return 'label label-primary';
    }
    if (status === 'next') {
      return 'label label-warning';
    }
    return 'label label-default';
  }

  loadStatus(): void {
    this.adminLoading = true;
    this.adminMessage = '';
    this.adminError = '';
    this.clientApiAdmin.status()
      .subscribe({
        next: response => {
          this.statusData = response && response.data ? response.data : null;
          this.adminMessage = 'Client API status loaded.';
          this.adminLoading = false;
        },
        error: error => this.handleAdminError(error, 'Client API status failed.')
      });
  }

  loadOperations(): void {
    this.adminLoading = true;
    this.adminMessage = '';
    this.adminError = '';
    this.clientApiAdmin.operations(25)
      .subscribe({
        next: response => {
          this.operationsData = response && response.data ? response.data : null;
          this.statusData = this.clientApiAdmin.runtimeFromOperations(response);
          this.adminMessage = 'Client API operations loaded.';
          this.adminLoading = false;
        },
        error: error => this.handleAdminError(error, 'Client API operations failed.')
      });
  }

  openSwagger(): void {
    this.openDocsLink('docs');
  }

  openOpenApi(): void {
    this.openDocsLink('openapi');
  }

  loadAdminDocsLinks(openKind?: 'docs' | 'openapi'): void {
    this.adminLoading = true;
    this.adminMessage = '';
    this.adminError = '';
    this.clientApiAdmin.docsLinks().subscribe({
      next: response => {
        this.adminDocsLinks = response && response.data ? response.data : null;
        this.adminLoading = false;
        if (!this.adminDocsLinks || !this.adminDocsLinks.docs_url) {
          this.adminError = 'Client API docs link is not configured.';
          return;
        }

        this.adminMessage = 'Admin docs links loaded.';
        if (openKind) {
          const url = openKind === 'openapi' ? this.adminDocsLinks.openapi_url : this.adminDocsLinks.docs_url;
          if (url) {
            window.open(url, '_blank', 'noopener');
          }
        }
      },
      error: error => this.handleAdminError(error, 'Client API docs link failed.')
    });
  }

  loadClients(): void {
    this.adminLoading = true;
    this.adminMessage = '';
    this.adminError = '';
    this.clientApiAdmin.listClients(this.selectedBaseUrl, 100)
      .subscribe({
        next: response => {
          this.clients = response && response.data && response.data.clients ? response.data.clients : [];
          this.adminMessage = 'Client list loaded.';
          this.adminLoading = false;
        },
        error: error => this.handleAdminError(error, 'Client list failed.')
      });
  }

  createClient(): void {
    if (!this.createModel.so_user_id || Number(this.createModel.so_user_id) < 1) {
      this.adminError = 'Enter the SO API user_id for this brand.';
      this.adminMessage = '';
      return;
    }

    const payload = {
      name: this.createModel.name,
      brand_id: Number(this.createModel.brand_id),
      so_user_id: Number(this.createModel.so_user_id),
      environment_mode: this.createModel.environment_mode,
      sync_enabled: this.createModel.sync_enabled,
      status: this.createModel.status,
      scopes: this.parseScopes(this.createModel.scopes)
    };

    this.adminLoading = true;
    this.clearGeneratedCredentials();
    this.adminMessage = '';
    this.adminError = '';
    this.clientApiAdmin.createClient(this.selectedBaseUrl, payload)
      .subscribe({
        next: response => {
          const client: ClientApiAdminClient | null = response && response.data ? response.data.client : null;
          this.generatedSecret = response && response.data ? response.data.client_secret : '';
          this.generatedClientUuid = client ? client.client_uuid : '';
          this.generatedClientName = client ? client.name : payload.name;
          this.generatedEnvironment = client ? client.environment_mode : payload.environment_mode;
          this.generatedBrandId = client ? Number(client.brand_id) : payload.brand_id;
          this.generatedAt = new Date().toISOString();
          this.adminMessage = 'Client created. Secret is visible once.';
          this.adminLoading = false;
          this.createModel.name = '';
          this.loadClients();
        },
        error: error => this.handleAdminError(error, 'Client create failed.')
      });
  }

  rotateSecret(client: ClientApiAdminClient): void {
    const confirmed = window.confirm(
      'Rotate the Client API secret for ' + client.name + '? Every existing secret and unexpired bearer token for this client will stop working immediately. Every connected application must be updated with the replacement secret.'
    );
    if (!confirmed) {
      return;
    }

    this.adminLoading = true;
    this.clearGeneratedCredentials();
    this.adminMessage = '';
    this.adminError = '';
    this.clientApiAdmin.rotateSecret(this.selectedBaseUrl, client.client_uuid, true, true).subscribe({
      next: response => {
        this.generatedSecret = response && response.data ? response.data.client_secret : '';
        this.generatedClientUuid = client.client_uuid;
        this.generatedClientName = client.name;
        this.generatedEnvironment = client.environment_mode;
        this.generatedBrandId = Number(client.brand_id);
        this.generatedAt = new Date().toISOString();
        this.adminMessage = 'Secret rotated. Save the replacement credentials now; every previous secret and bearer token has been revoked.';
        this.adminLoading = false;
        this.loadClients();
      },
      error: error => this.handleAdminError(error, 'Secret rotation failed.')
    });
  }

  copyValue(value: string, label: string): void {
    if (!value) {
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(() => {
        this.adminMessage = label + ' copied.';
      }).catch(() => this.copyValueFallback(value, label));
      return;
    }

    this.copyValueFallback(value, label);
  }

  copyCredentials(): void {
    this.copyValue(this.credentialsText(), 'Client API setup');
  }

  downloadCredentials(): void {
    if (!this.generatedSecret || !this.generatedClientUuid) {
      return;
    }

    const blob = new Blob([this.credentialsText()], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sharmans-client-api-' + this.safeFilePart(this.generatedClientName) + '-' + this.safeFilePart(this.generatedEnvironment) + '-credentials.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    this.adminMessage = 'One-time credential file downloaded. Move it to an approved secret manager, then delete the downloaded plaintext file.';
  }

  canLaunchPlayground(client: ClientApiAdminClient): boolean {
    return client.status === 'active'
      && !client.access_blocked;
  }

  canLaunchLiveTest(client: ClientApiAdminClient): boolean {
    return client.status === 'active'
      && !client.access_blocked
      && client.environment_mode === 'live'
      && client.sync_enabled
      && client.live_test_enabled
      && client.scopes.indexOf('jobs:create') >= 0;
  }

  private openHostedLaunchWindow(): Window | null {
    const launchWindow = window.open('', '_blank');
    if (!launchWindow || launchWindow.closed) {
      return null;
    }

    launchWindow.document.title = 'Opening My Investigations Corp';
    launchWindow.document.body.style.fontFamily = 'Arial, sans-serif';
    launchWindow.document.body.style.padding = '32px';
    launchWindow.document.body.textContent = 'Opening your secure hosted session...';
    return launchWindow;
  }

  launchPlayground(client: ClientApiAdminClient): void {
    if (!this.canLaunchPlayground(client)) {
      this.adminError = 'The hosted playground requires an active, unblocked client. The launched session is always isolated sandbox mode.';
      return;
    }
    const launchWindow = this.openHostedLaunchWindow();
    if (!launchWindow) {
      this.adminError = 'A new window is required so this page stays open. Allow pop-ups for Sharmans.Online, then try again.';
      return;
    }
    this.adminLoading = true;
    this.adminMessage = '';
    this.adminError = '';
    this.clientApiAdmin.playgroundLaunch(client.client_uuid, Number(client.brand_id)).subscribe({
      next: response => {
        const launchUrl = response && response.data ? response.data.launch_url : '';
        this.adminLoading = false;
        if (!launchUrl) {
          launchWindow.close();
          this.adminError = 'The hosted playground launch URL was not returned.';
          return;
        }
        launchWindow.location.href = launchUrl;
        launchWindow.opener = null;
        launchWindow.focus();
      },
      error: error => {
        launchWindow.close();
        this.handleAdminError(error, 'The hosted sandbox playground could not be opened.');
      }
    });
  }

  launchLiveTest(client: ClientApiAdminClient): void {
    if (!this.canLaunchLiveTest(client)) {
      this.adminError = 'Live Test must be enabled for this brand and requires an active, unblocked live credential with Sync writes and jobs:create.';
      return;
    }

    const launchWindow = this.openHostedLaunchWindow();
    if (!launchWindow) {
      this.adminError = 'A new window is required so this page stays open. Allow pop-ups for Sharmans.Online, then try again.';
      return;
    }
    window.focus();

    const confirmation = window.prompt(
      'LIVE TEST creates real SO jobs, notes and files and can trigger normal automation and notifications. Type LIVE to continue.'
    );
    if (confirmation !== 'LIVE') {
      launchWindow.close();
      return;
    }

    this.adminLoading = true;
    this.adminMessage = '';
    this.adminError = '';
    this.clientApiAdmin.liveTestLaunch(client.client_uuid, Number(client.brand_id)).subscribe({
      next: response => {
        const launchUrl = response && response.data ? response.data.launch_url : '';
        this.adminLoading = false;
        if (!launchUrl) {
          launchWindow.close();
          this.adminError = 'The hosted Live Test launch URL was not returned.';
          return;
        }
        launchWindow.location.href = launchUrl;
        launchWindow.opener = null;
        launchWindow.focus();
      },
      error: error => {
        launchWindow.close();
        this.handleAdminError(error, 'The hosted Live Test session could not be opened.');
      }
    });
  }

  traceText(trace: any): string {
    if (!trace) {
      return '';
    }

    const parts = [];
    ['queue_id', 'job_request_id', 'external_reference', 'so_job_id', 'subject_id', 'subject_number', 'so_user_id', 'api_actor_user_id', 'user_id', 'so_log_id', 'log_type_id', 'driver', 'reason'].forEach(key => {
      if (trace[key] !== undefined && trace[key] !== null && trace[key] !== '') {
        parts.push(key + ': ' + trace[key]);
      }
    });

    if (trace.automation_ids && trace.automation_ids.length) {
      parts.push('automation_ids: ' + trace.automation_ids.join(', '));
    }

    if (trace.executions && trace.executions.length) {
      const executions = trace.executions
        .filter(item => item)
        .map(item => (item.system_automation_id || '?') + ' ' + (item.ok === true ? 'ok' : 'failed') + ' ' + (item.status_code || ''));
      if (executions.length) {
        parts.push('executions: ' + executions.join('; '));
      }
    }

    return parts.join(' | ');
  }

  private openDocsLink(kind: 'docs' | 'openapi'): void {
    const localUrl = kind === 'openapi'
      ? this.clientApiAdmin.openApiUrlFor(this.selectedBaseUrl)
      : this.clientApiAdmin.docsUrlFor(this.selectedBaseUrl);

    if (this.selectedBaseUrl.replace(/\/+$/, '') === this.localBaseUrl.replace(/\/+$/, '')) {
      window.open(localUrl, '_blank', 'noopener');
      return;
    }

    this.loadAdminDocsLinks(kind);
  }

  private parseScopes(raw: string): string[] {
    return raw
      .split(/[,\s]+/)
      .map(scope => scope.trim())
      .filter(scope => scope !== '');
  }

  private clearGeneratedCredentials(): void {
    this.generatedSecret = '';
    this.generatedClientUuid = '';
    this.generatedClientName = '';
    this.generatedEnvironment = '';
    this.generatedBrandId = 0;
    this.generatedAt = '';
  }

  private copyValueFallback(value: string, label: string): void {
    const field = document.createElement('textarea');
    field.value = value;
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    document.execCommand('copy');
    document.body.removeChild(field);
    this.adminMessage = label + ' copied.';
  }

  private credentialsText(): string {
    const baseUrl = this.selectedBaseUrl.replace(/\/+$/, '');
    return [
      'Sharmans Client API one-time credentials',
      '',
      'API client: ' + this.generatedClientName,
      'Environment: ' + this.generatedEnvironment,
      'Created: ' + this.generatedAt,
      '',
      'CLIENT_API_BASE_URL=' + baseUrl,
      'CLIENT_API_CLIENT_ID=' + this.generatedClientUuid,
      'CLIENT_API_CLIENT_SECRET=' + this.generatedSecret,
      'CLIENT_API_PORTAL_URL=' + this.clientApiAdmin.portalUrlForBrand(this.generatedBrandId, baseUrl),
      'CLIENT_API_SAMPLE_APP_URL=' + this.clientApiAdmin.sampleAppUrlFor(baseUrl),
      '',
      'IMPORTANT',
      '- This secret is displayed once and cannot be retrieved later.',
      '- Store it in the integrating application server-side secret manager or protected environment configuration.',
      '- Never place it in Angular/browser code, a URL, source control, email or a support ticket.',
      '- Open Guide and Swagger from API Access & Controls so the broker can issue the correct authenticated links.',
      '- If this secret was rotated, every previous secret and unexpired bearer token stopped working immediately.',
      '- After secure storage, delete this plaintext file from Downloads and any temporary location.'
    ].join('\r\n');
  }

  private safeFilePart(value: string): string {
    const safe = String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return safe || 'client';
  }

  private handleAdminError(error: any, fallback: string): void {
    const apiErrors = error && error.error && Array.isArray(error.error.errors)
      ? error.error.errors
      : [];
    const primaryError = apiErrors.length ? apiErrors[0] : null;
    const details = primaryError && Array.isArray(primaryError.details)
      ? primaryError.details
      : [];
    const detailMessages = details
      .map(detail => detail && detail.message ? detail.message : '')
      .filter(message => message !== '');
    const responseError = detailMessages.length
      ? detailMessages.join(' ')
      : (primaryError && primaryError.message ? primaryError.message : '');
    this.adminError = responseError || fallback;
    this.adminMessage = '';
    this.adminLoading = false;
  }
}
